import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { db, storage } from '@/lib/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/useToast';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon, Loader2, ChevronDown, ChevronUp, ShoppingBag } from 'lucide-react';
import { initializeStoreProducts } from '@/lib/initializeStoreProducts';
import StoreProductImageCarousel from '@/features/store/components/StoreProductImageCarousel';
import { formatUkDateTime } from '@/lib/adminFormat';

const AdminStoreManager = () => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingImageFor, setUploadingImageFor] = useState(null); // Track which product is uploading
  const fileInputRef = useRef(null);
  const fileInputRef2 = useRef(null);
  const editFileInputRef = useRef(null);
  const editFileInputRef2 = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    free_claim_threshold_gbp: '',
    image_url: '',
    image_url_2: '',
    order: 0
  });
  const [purchasesOpen, setPurchasesOpen] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  useEffect(() => {
    const initializeAndLoad = async () => {
      try {
        setLoading(true);
        // First, try to initialize products if they don't exist
        const result = await initializeStoreProducts();
        if (result.success) {
          console.log('Initialization result:', result);
        }
        // Then load products
        await loadProducts();
        await loadPurchases();
      } catch (error) {
        console.error('Error initializing or loading products:', error);
        await loadProducts();
        await loadPurchases();
      }
    };
    initializeAndLoad();
  }, []);

  const handleInitializeProducts = async () => {
    try {
      setLoading(true);
      const result = await initializeStoreProducts(true);
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Products initialized successfully!',
        });
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to initialize products.',
          variant: 'destructive'
        });
      }
      await loadProducts();
      await loadPurchases();
    } catch (error) {
      console.error('Error initializing products:', error);
      toast({
        title: 'Error',
        description: 'Failed to initialize products.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const productsRef = collection(db, 'store_products');
      const productsSnapshot = await getDocs(productsRef);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      productsData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
      toast({
        title: 'Error',
        description: 'Failed to load products.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPurchases = async () => {
    try {
      setLoadingPurchases(true);
      const purchasesRef = collection(db, 'store_purchases');
      let snap;
      try {
        snap = await getDocs(
          query(purchasesRef, orderBy('created_at', 'desc'), limit(100))
        );
      } catch (orderErr) {
        console.warn('store_purchases orderBy, falling back:', orderErr);
        snap = await getDocs(purchasesRef);
      }
      const rows = snap.docs.map((d) => {
        const data = d.data();
        const created = data.created_at?.toDate ? data.created_at.toDate() : null;
        return { id: d.id, ...data, created_at: created };
      });
      rows.sort((a, b) => {
        const ta = a.created_at instanceof Date ? a.created_at.getTime() : 0;
        const tb = b.created_at instanceof Date ? b.created_at.getTime() : 0;
        return tb - ta;
      });
      setPurchases(rows.slice(0, 100));
    } catch (e) {
      console.error('loadPurchases', e);
      toast({
        title: 'Could not load store purchases',
        description: e?.message || 'Check Firestore rules.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPurchases(false);
    }
  };

  const handleAddProduct = async () => {
    if (!formData.name || !formData.price) {
      toast({
        title: 'Error',
        description: 'Please fill in name and price.',
        variant: 'destructive'
      });
      return;
    }

    const priceNum = parseFloat(formData.price);
    const thresholdNum =
      formData.free_claim_threshold_gbp !== '' && formData.free_claim_threshold_gbp != null
        ? parseFloat(formData.free_claim_threshold_gbp)
        : priceNum;

    if (!Number.isFinite(thresholdNum) || thresholdNum <= 0) {
      toast({
        title: 'Error',
        description: 'Set a valid “free claim” earnings threshold (GBP).',
        variant: 'destructive',
      });
      return;
    }

    try {
      await addDoc(collection(db, 'store_products'), {
        name: formData.name,
        description: formData.description || '',
        price: priceNum,
        free_claim_threshold_gbp: thresholdNum,
        image_url: formData.image_url || '',
        image_url_2: formData.image_url_2 || '',
        order: parseInt(formData.order) || products.length,
        created_at: new Date(),
        updated_at: new Date()
      });

      toast({
        title: 'Success',
        description: 'Product added successfully.',
      });

      setFormData({
        name: '',
        description: '',
        price: '',
        free_claim_threshold_gbp: '',
        image_url: '',
        image_url_2: '',
        order: 0
      });
      setShowAddForm(false);
      loadProducts();
      loadPurchases();
    } catch (error) {
      console.error('Error adding product:', error);
      toast({
        title: 'Error',
        description: 'Failed to add product.',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateProduct = async (productId) => {
    const product = editingProduct;
    if (!product.name || product.price === '' || product.price == null) {
      toast({
        title: 'Error',
        description: 'Please fill in name and price.',
        variant: 'destructive'
      });
      return;
    }

    const priceNum = parseFloat(product.price);
    const thresholdNum =
      product.free_claim_threshold_gbp !== '' &&
      product.free_claim_threshold_gbp !== undefined &&
      product.free_claim_threshold_gbp !== null
        ? parseFloat(product.free_claim_threshold_gbp)
        : priceNum;

    if (!Number.isFinite(thresholdNum) || thresholdNum <= 0) {
      toast({
        title: 'Error',
        description: 'Set a valid free-claim earnings threshold.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateDoc(doc(db, 'store_products', productId), {
        name: product.name,
        description: product.description || '',
        price: priceNum,
        free_claim_threshold_gbp: thresholdNum,
        image_url: product.image_url || '',
        image_url_2: product.image_url_2 || '',
        order: parseInt(product.order) || 0,
        updated_at: new Date()
      });

      toast({
        title: 'Success',
        description: 'Product updated successfully.',
      });

      setEditingProduct(null);
      loadProducts();
      loadPurchases();
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: 'Error',
        description: 'Failed to update product.',
        variant: 'destructive'
      });
    }
  };

  const handleImageUpload = async (e, isEditMode = false, imageSlot = 1) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please select an image file.',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be less than 5MB.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploadingImage(true);
      if (isEditMode) {
        setUploadingImageFor(editingProduct?.id);
      }

      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `store-product-${timestamp}-slot${imageSlot}-${file.name}`;
      const storageRef = ref(storage, `store_products/${fileName}`);

      // Upload file
      await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update form data or editing product
      if (isEditMode) {
        if (imageSlot === 2) {
          setEditingProduct({ ...editingProduct, image_url_2: downloadURL });
        } else {
          setEditingProduct({ ...editingProduct, image_url: downloadURL });
        }
      } else if (imageSlot === 2) {
        setFormData({ ...formData, image_url_2: downloadURL });
      } else {
        setFormData({ ...formData, image_url: downloadURL });
      }

      toast({
        title: 'Success',
        description: 'Image uploaded successfully!',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload Failed',
        description: 'Could not upload image. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUploadingImage(false);
      setUploadingImageFor(null);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (fileInputRef2.current) fileInputRef2.current.value = '';
      if (editFileInputRef.current) editFileInputRef.current.value = '';
      if (editFileInputRef2.current) editFileInputRef2.current.value = '';
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'store_products', productId));
      toast({
        title: 'Success',
        description: 'Product deleted successfully.',
      });
      loadProducts();
      loadPurchases();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete product.',
        variant: 'destructive'
      });
    }
  };

  const startEdit = (product) => {
    setEditingProduct({ ...product });
  };

  const cancelEdit = () => {
    setEditingProduct(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold">Store Products</h2>
          <p className="text-gray-400 text-sm">Manage store products and pricing</p>
        </div>
        <div className="flex gap-2">
          {products.length === 0 && (
            <Button
              onClick={handleInitializeProducts}
              className="bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              <Plus className="w-4 h-4 mr-2" />
              Initialize Default Products
            </Button>
          )}
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      <Card className="border-gray-700 bg-gray-800">
        <CardHeader className="space-y-0 pb-2">
          <button
            type="button"
            onClick={() => setPurchasesOpen((o) => !o)}
            className="flex w-full items-start justify-between gap-3 rounded-lg p-1 text-left transition-colors hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
            aria-expanded={purchasesOpen}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <ShoppingBag className="h-5 w-5 shrink-0 text-purple-400" />
              <CardTitle className="mb-0 text-lg">Purchases &amp; free claims</CardTitle>
              {purchases.length > 0 ? (
                <Badge variant="outline" className="border-gray-500 text-gray-300">
                  {purchases.length}
                </Badge>
              ) : null}
            </div>
            {purchasesOpen ? (
              <ChevronUp className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            ) : (
              <ChevronDown className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            )}
          </button>
          <CardDescription className={purchasesOpen ? 'mt-2' : 'hidden'}>
            Paid checkouts (A2B) and free claims from artists. Newest first.
          </CardDescription>
        </CardHeader>
        {purchasesOpen ? (
          <CardContent className="pt-0">
            <div className="mb-3 flex justify-end">
              <Button type="button" size="sm" variant="outline" onClick={() => loadPurchases()}>
                Refresh
              </Button>
            </div>
            {loadingPurchases ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
              </div>
            ) : purchases.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">
                No purchases or claims yet.
              </p>
            ) : (
              <div className="max-h-[24rem] space-y-2 overflow-y-auto overscroll-contain pr-1">
                {purchases.map((row) => (
                  <div
                    key={row.id}
                    className="rounded-lg border border-gray-600/60 bg-gray-700/50 p-3 text-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-white">
                        {row.product_name || '—'} ({row.product_id || '—'})
                      </span>
                      <Badge
                        variant="outline"
                        className={
                          row.purchase_type === 'free_claim'
                            ? 'border-green-600 text-green-300'
                            : 'border-blue-600 text-blue-300'
                        }
                      >
                        {row.purchase_type === 'free_claim' ? 'Free claim' : 'Paid'}
                      </Badge>
                    </div>
                    <p className="mt-1 text-gray-300">
                      <span className="text-gray-500">Artist:</span>{' '}
                      {row.artist_name || '—'}{' '}
                      <span className="text-gray-500">&lt;{row.artist_email || '—'}&gt;</span>
                    </p>
                    {row.purchase_type === 'paid' && (
                      <>
                        <p className="mt-1 text-gray-400">
                          £{Number(row.amount_paid || 0).toFixed(2)} {row.currency || 'GBP'}
                        </p>
                        {(row.shipping_line1 || row.shipping_city) && (
                          <p className="mt-1 text-xs text-gray-500">
                            Ship to: {row.shipping_full_name}, {row.shipping_line1}
                            {row.shipping_line2 ? `, ${row.shipping_line2}` : ''}, {row.shipping_city},{' '}
                            {row.shipping_postcode}. Tel: {row.shipping_phone || '—'}
                          </p>
                        )}
                      </>
                    )}
                    {row.purchase_type === 'free_claim' && row.request_earnings_at_claim != null && (
                      <p className="mt-1 text-xs text-gray-500">
                        Earnings at claim: £{Number(row.request_earnings_at_claim).toFixed(2)} (threshold
                        £{Number(row.free_claim_threshold_gbp || 0).toFixed(2)})
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {row.created_at instanceof Date && !Number.isNaN(row.created_at.getTime())
                        ? formatUkDateTime(row.created_at)
                        : '—'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        ) : null}
      </Card>

      {/* Add Product Form */}
      {showAddForm && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle>Add New Product</CardTitle>
            <CardDescription>Enter product details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label htmlFor="store-add-name" className="text-sm font-medium text-gray-300 mb-1 block">
                Product name
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                Main heading on the store. <span className="text-gray-400">Field:</span>{' '}
                <code className="text-gray-400 text-[11px]">name</code>
              </p>
              <Input
                id="store-add-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Branded beer mats"
                className="bg-gray-700 border-gray-600"
              />
            </div>
            <div>
              <label htmlFor="store-add-description" className="text-sm font-medium text-gray-300 mb-1 block">
                Description (subtitle)
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                Short line under the title—materials, size, or pitch. <span className="text-gray-400">Field:</span>{' '}
                <code className="text-gray-400 text-[11px]">description</code>
              </p>
              <Input
                id="store-add-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g. Pack of 50, full colour"
                className="bg-gray-700 border-gray-600"
              />
            </div>
            <div>
              <label htmlFor="store-add-price" className="text-sm font-medium text-gray-300 mb-1 block">
                Price (£)
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                What customers pay (GBP). Used as the default free-claim target if the field below is empty.{' '}
                <span className="text-gray-400">Field:</span>{' '}
                <code className="text-gray-400 text-[11px]">price</code>
              </p>
              <Input
                id="store-add-price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="12.99"
                className="bg-gray-700 border-gray-600"
              />
            </div>
            <div>
              <label htmlFor="store-add-threshold" className="text-sm font-medium text-gray-300 mb-1 block">
                Free claim — request earnings target (£)
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                How much an artist must earn from <em>song requests</em> before they can claim this item free. Can match RRP or
                be different (e.g. promotional). Leave blank to use the price above.{' '}
                <span className="text-gray-400">Field:</span>{' '}
                <code className="text-gray-400 text-[11px]">free_claim_threshold_gbp</code>
              </p>
              <Input
                id="store-add-threshold"
                type="number"
                step="0.01"
                value={formData.free_claim_threshold_gbp}
                onChange={(e) =>
                  setFormData({ ...formData, free_claim_threshold_gbp: e.target.value })
                }
                placeholder="Defaults to price if left blank"
                className="bg-gray-700 border-gray-600"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Artists see copy like “Earn £X in requests…” on the store.
              </p>
            </div>
            <div>
              <label htmlFor="store-add-image-url" className="text-sm font-medium text-gray-300 mb-1 block">
                Product image 1
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                Primary photo on the store card.{' '}
                <span className="text-gray-400">Field:</span>{' '}
                <code className="text-gray-400 text-[11px]">image_url</code>
              </p>
              <div className="flex items-center gap-2">
                <Input
                  id="store-add-image-url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://… or upload →"
                  className="bg-gray-700 border-gray-600 flex-1"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, false, 1)}
                  className="hidden"
                  id="image-upload-add"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white"
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-1" />
                      Upload 1
                    </>
                  )}
                </Button>
              </div>
              {formData.image_url && (
                <div className="mt-2 w-32 h-32 rounded-lg overflow-hidden bg-gray-700">
                  <img
                    src={formData.image_url}
                    alt="Preview 1"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <label htmlFor="store-add-image-url-2" className="text-sm font-medium text-gray-300 mb-1 block">
                Product image 2 <span className="font-normal text-gray-500">(optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                Second photo. If set, the store cycles between image 1 and 2 every few seconds.{' '}
                <span className="text-gray-400">Field:</span>{' '}
                <code className="text-gray-400 text-[11px]">image_url_2</code>
              </p>
              <div className="flex items-center gap-2">
                <Input
                  id="store-add-image-url-2"
                  value={formData.image_url_2}
                  onChange={(e) => setFormData({ ...formData, image_url_2: e.target.value })}
                  placeholder="https://… or upload →"
                  className="bg-gray-700 border-gray-600 flex-1"
                />
                <input
                  ref={fileInputRef2}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, false, 2)}
                  className="hidden"
                  id="image-upload-add-2"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef2.current?.click()}
                  disabled={uploadingImage}
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white"
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-1" />
                      Upload 2
                    </>
                  )}
                </Button>
              </div>
              {formData.image_url_2 && (
                <div className="mt-2 w-32 h-32 rounded-lg overflow-hidden bg-gray-700">
                  <img
                    src={formData.image_url_2}
                    alt="Preview 2"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <label htmlFor="store-add-order" className="text-sm font-medium text-gray-300 mb-1 block">
                Display order
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                Sort order on the store page (lower numbers first; ties fall back to creation).{' '}
                <span className="text-gray-400">Field:</span>{' '}
                <code className="text-gray-400 text-[11px]">order</code>
              </p>
              <Input
                id="store-add-order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                placeholder="0"
                className="bg-gray-700 border-gray-600"
              />
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddProduct} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Save Product
              </Button>
              <Button onClick={() => setShowAddForm(false)} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products List */}
      {products.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-8 text-center">
            <p className="text-gray-400">No products yet. Add your first product above.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <Card key={product.id} className="bg-gray-800 border-gray-700">
              {editingProduct?.id === product.id ? (
                <CardContent className="p-4 space-y-4">
                  <div>
                    <label htmlFor={`store-edit-name-${product.id}`} className="text-sm font-medium text-gray-300 mb-1 block">
                      Product name
                    </label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Main heading on the store. <span className="text-gray-400">Field:</span>{' '}
                      <code className="text-gray-400 text-[11px]">name</code>
                    </p>
                    <Input
                      id={`store-edit-name-${product.id}`}
                      value={editingProduct.name}
                      onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                      placeholder="e.g. Branded beer mats"
                      className="bg-gray-700 border-gray-600"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`store-edit-description-${product.id}`}
                      className="text-sm font-medium text-gray-300 mb-1 block"
                    >
                      Description (subtitle)
                    </label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Short line under the title. <span className="text-gray-400">Field:</span>{' '}
                      <code className="text-gray-400 text-[11px]">description</code>
                    </p>
                    <Input
                      id={`store-edit-description-${product.id}`}
                      value={editingProduct.description || ''}
                      onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                      placeholder="e.g. Pack of 50, full colour"
                      className="bg-gray-700 border-gray-600"
                    />
                  </div>
                  <div>
                    <label htmlFor={`store-edit-price-${product.id}`} className="text-sm font-medium text-gray-300 mb-1 block">
                      Price (£)
                    </label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Customer price in GBP; default free-claim target if threshold is blank.{' '}
                      <span className="text-gray-400">Field:</span>{' '}
                      <code className="text-gray-400 text-[11px]">price</code>
                    </p>
                    <Input
                      id={`store-edit-price-${product.id}`}
                      type="number"
                      step="0.01"
                      value={editingProduct.price}
                      onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
                      placeholder="12.99"
                      className="bg-gray-700 border-gray-600"
                    />
                  </div>
                  <div>
                    <label htmlFor={`store-edit-threshold-${product.id}`} className="text-sm font-medium text-gray-300 mb-1 block">
                      Free claim — request earnings target (£)
                    </label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Request earnings needed before an artist can claim this item free. Blank = use price.{' '}
                      <span className="text-gray-400">Field:</span>{' '}
                      <code className="text-gray-400 text-[11px]">free_claim_threshold_gbp</code>
                    </p>
                    <Input
                      id={`store-edit-threshold-${product.id}`}
                      type="number"
                      step="0.01"
                      value={
                        editingProduct.free_claim_threshold_gbp !== undefined &&
                        editingProduct.free_claim_threshold_gbp !== null
                          ? editingProduct.free_claim_threshold_gbp
                          : ''
                      }
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          free_claim_threshold_gbp: e.target.value,
                        })
                      }
                      className="bg-gray-700 border-gray-600"
                      placeholder="Blank = same as price"
                    />
                  </div>
                  <div>
                    <label htmlFor={`store-edit-image-${product.id}`} className="text-sm font-medium text-gray-300 mb-1 block">
                      Product image 1
                    </label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Primary photo. <span className="text-gray-400">Field:</span>{' '}
                      <code className="text-gray-400 text-[11px]">image_url</code>
                    </p>
                    <Input
                      id={`store-edit-image-${product.id}`}
                      value={editingProduct.image_url || ''}
                      onChange={(e) => setEditingProduct({ ...editingProduct, image_url: e.target.value })}
                      placeholder="https://…"
                      className="bg-gray-700 border-gray-600"
                    />
                  </div>
                  <div>
                    <label htmlFor={`store-edit-image-2-${product.id}`} className="text-sm font-medium text-gray-300 mb-1 block">
                      Product image 2 <span className="font-normal text-gray-500">(optional)</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Second photo for carousel on the store.{' '}
                      <span className="text-gray-400">Field:</span>{' '}
                      <code className="text-gray-400 text-[11px]">image_url_2</code>
                    </p>
                    <Input
                      id={`store-edit-image-2-${product.id}`}
                      value={editingProduct.image_url_2 || ''}
                      onChange={(e) => setEditingProduct({ ...editingProduct, image_url_2: e.target.value })}
                      placeholder="https://…"
                      className="bg-gray-700 border-gray-600"
                    />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-300 mb-1 block">Upload image files</span>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Upload 1 → <code className="text-gray-400 text-[11px]">image_url</code>
                      . Upload 2 → <code className="text-gray-400 text-[11px]">image_url_2</code>.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        ref={editFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, true, 1)}
                        className="hidden"
                        id={`image-upload-edit-${product.id}`}
                      />
                      <input
                        ref={editFileInputRef2}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, true, 2)}
                        className="hidden"
                        id={`image-upload-edit-2-${product.id}`}
                      />
                      <Button
                        type="button"
                        onClick={() => editFileInputRef.current?.click()}
                        disabled={uploadingImage}
                        size="sm"
                        variant="outline"
                        className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white"
                      >
                        {uploadingImage && uploadingImageFor === product.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-3 h-3 mr-1" />
                            Upload 1
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => editFileInputRef2.current?.click()}
                        disabled={uploadingImage}
                        size="sm"
                        variant="outline"
                        className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white"
                      >
                        {uploadingImage && uploadingImageFor === product.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-3 h-3 mr-1" />
                            Upload 2
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  {(editingProduct.image_url || editingProduct.image_url_2) && (
                    <div>
                      <span className="text-sm font-medium text-gray-300 mb-1 block">Preview</span>
                      <p className="text-xs text-gray-500 mb-1.5">Images as they appear on the store card.</p>
                      <div className="flex flex-wrap gap-2">
                        {editingProduct.image_url && (
                          <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-700 shrink-0">
                            <img
                              src={editingProduct.image_url}
                              alt="Preview 1"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        {editingProduct.image_url_2 && (
                          <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-700 shrink-0">
                            <img
                              src={editingProduct.image_url_2}
                              alt="Preview 2"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <label htmlFor={`store-edit-order-${product.id}`} className="text-sm font-medium text-gray-300 mb-1 block">
                      Display order
                    </label>
                    <p className="text-xs text-gray-500 mb-1.5">
                      Lower numbers first on the store. <span className="text-gray-400">Field:</span>{' '}
                      <code className="text-gray-400 text-[11px]">order</code>
                    </p>
                    <Input
                      id={`store-edit-order-${product.id}`}
                      type="number"
                      value={editingProduct.order || 0}
                      onChange={(e) => setEditingProduct({ ...editingProduct, order: e.target.value })}
                      className="bg-gray-700 border-gray-600"
                      placeholder="0"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleUpdateProduct(product.id)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Save
                    </Button>
                    <Button
                      onClick={cancelEdit}
                      size="sm"
                      variant="outline"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <>
                  <StoreProductImageCarousel
                    product={product}
                    alt={product.name}
                    heightClass="h-32"
                  />
                  <CardHeader>
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                    <CardDescription className="text-gray-400 text-sm">
                      {product.description || 'No description'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-purple-400 mb-1">
                      £{parseFloat(product.price || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      Free claim at £
                      {parseFloat(
                        product.free_claim_threshold_gbp ?? product.price ?? 0
                      ).toFixed(2)}{' '}
                      request earnings
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => startEdit(product)}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDeleteProduct(product.id)}
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminStoreManager;

